import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react'

export const SEPOLIA_CHAIN_ID = '0xaa36a7'

// Marks that the user explicitly disconnected in-app, so we don't silently
// re-attach the still-authorized MetaMask account on the next page load.
const DISCONNECTED_KEY = 'wallet-disconnected'
const wasManuallyDisconnected = () => {
  try {
    return localStorage.getItem(DISCONNECTED_KEY) === '1'
  } catch {
    return false
  }
}
const setManuallyDisconnected = (v: boolean) => {
  try {
    if (v) localStorage.setItem(DISCONNECTED_KEY, '1')
    else localStorage.removeItem(DISCONNECTED_KEY)
  } catch {
    // ignore (e.g. storage disabled)
  }
}

export interface WalletContextValue {
  address: string | null
  chainId: string | null
  isConnected: boolean
  isCorrectNetwork: boolean
  isConnecting: boolean
  isMetaMaskInstalled: boolean
  error: string | null
  isModalOpen: boolean
  connect: () => Promise<void>
  disconnect: () => void
  switchToSepolia: () => Promise<void>
  openConnectModal: () => void
  closeConnectModal: () => void
}

const WalletContext = createContext<WalletContextValue | null>(null)

export function WalletProvider({ children }: { children: ReactNode }) {
  const [address, setAddress] = useState<string | null>(null)
  const [chainId, setChainId] = useState<string | null>(null)
  const [isConnecting, setIsConnecting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isMetaMaskInstalled, setIsMetaMaskInstalled] = useState(false)

  // Detect MetaMask on mount
  useEffect(() => {
    setIsMetaMaskInstalled(Boolean(window.ethereum?.isMetaMask))
  }, [])

  // Restore existing connection without prompting
  useEffect(() => {
    const eth = window.ethereum
    if (!eth) return
    if (wasManuallyDisconnected()) return // respect explicit logout across reloads
    void (async () => {
      try {
        const accounts = (await eth.request({ method: 'eth_accounts' })) as string[]
        if (accounts.length > 0) {
          setAddress(accounts[0].toLowerCase())
          const id = (await eth.request({ method: 'eth_chainId' })) as string
          setChainId(id)
        }
      } catch {
        // Silently ignore — user has not connected yet
      }
    })()
  }, [])

  // Listen to account / chain changes
  useEffect(() => {
    const eth = window.ethereum
    if (!eth) return

    const handleAccountsChanged = (accounts: unknown) => {
      const acc = accounts as string[]
      if (acc.length === 0) {
        setAddress(null)
        setChainId(null)
      } else if (!wasManuallyDisconnected()) {
        setAddress(acc[0].toLowerCase())
      }
    }

    const handleChainChanged = (id: unknown) => {
      setChainId(id as string)
    }

    eth.on('accountsChanged', handleAccountsChanged)
    eth.on('chainChanged', handleChainChanged)

    return () => {
      eth.removeListener('accountsChanged', handleAccountsChanged)
      eth.removeListener('chainChanged', handleChainChanged)
    }
  }, [])

  const connect = useCallback(async () => {
    const eth = window.ethereum
    if (!eth) {
      setError('MetaMask가 설치되지 않았습니다')
      return
    }
    setIsConnecting(true)
    setError(null)
    setManuallyDisconnected(false) // explicit connect clears the logout flag
    try {
      // Force the MetaMask account picker even when the site is already authorized,
      // so the user can switch accounts (e.g. to the admin wallet) instead of being
      // silently reconnected to the previously selected one.
      try {
        await eth.request({
          method: 'wallet_requestPermissions',
          params: [{ eth_accounts: {} }],
        })
      } catch (permErr: unknown) {
        // 4001 = user rejected the picker; abort. Other errors (e.g. method not
        // supported by the wallet) fall through to eth_requestAccounts below.
        if ((permErr as { code?: number })?.code === 4001) {
          setError('연결을 취소했습니다')
          return
        }
      }
      const accounts = (await eth.request({ method: 'eth_requestAccounts' })) as string[]
      if (accounts.length > 0) {
        setAddress(accounts[0].toLowerCase())
        const id = (await eth.request({ method: 'eth_chainId' })) as string
        setChainId(id)
        if (id === SEPOLIA_CHAIN_ID) {
          setIsModalOpen(false)
        }
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : '연결에 실패했습니다')
    } finally {
      setIsConnecting(false)
    }
  }, [])

  const disconnect = useCallback(() => {
    setManuallyDisconnected(true)
    setAddress(null)
    setChainId(null)
    setError(null)
  }, [])

  const switchToSepolia = useCallback(async () => {
    const eth = window.ethereum
    if (!eth) return
    try {
      await eth.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: SEPOLIA_CHAIN_ID }],
      })
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : '네트워크 전환에 실패했습니다')
    }
  }, [])

  const openConnectModal = useCallback(() => setIsModalOpen(true), [])
  const closeConnectModal = useCallback(() => {
    setIsModalOpen(false)
    setError(null)
  }, [])

  const isConnected = Boolean(address)
  const isCorrectNetwork = chainId === SEPOLIA_CHAIN_ID

  return (
    <WalletContext.Provider
      value={{
        address,
        chainId,
        isConnected,
        isCorrectNetwork,
        isConnecting,
        isMetaMaskInstalled,
        error,
        isModalOpen,
        connect,
        disconnect,
        switchToSepolia,
        openConnectModal,
        closeConnectModal,
      }}
    >
      {children}
    </WalletContext.Provider>
  )
}

export function useWallet(): WalletContextValue {
  const ctx = useContext(WalletContext)
  if (!ctx) throw new Error('useWallet must be used within WalletProvider')
  return ctx
}
